'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Pause, Play, Volume2, VolumeX } from 'lucide-react';

interface AudioPlayerProps {
  fileId: string;
  fileName: string;
  /** 내장 앨범아트가 있을 때만 전달 (없으면 음표 아이콘 폴백) */
  coverUrl?: string;
  title?: string;
  artist?: string;
  album?: string;
  /** media-info에서 온 재생 시간. 메타데이터 로드 전 파형 폭 계산에 쓴다. */
  durationHint?: number;
}

const BAR_WIDTH = 3;
const BAR_GAP = 2;

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function AudioPlayer({
  fileId,
  fileName,
  coverUrl,
  title,
  artist,
  album,
  durationHint,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [waveformFailed, setWaveformFailed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationHint ?? 0);
  const [muted, setMuted] = useState(false);
  const [coverFailed, setCoverFailed] = useState(false);
  const [seekPreview, setSeekPreview] = useState<number | null>(null);
  const [width, setWidth] = useState(0);
  const [isDark, setIsDark] = useState(false);

  const displayTime = seekPreview ?? currentTime;
  const progress = duration > 0 ? Math.min(1, displayTime / duration) : 0;

  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setIsDark(el.classList.contains('dark'));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/waveform/${fileId}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('waveform unavailable'))))
      .then((data: { peaks: number[]; duration: number }) => {
        if (cancelled) return;
        setPeaks(data.peaks);
        setDuration((prev) => (prev > 0 ? prev : data.duration));
      })
      .catch(() => {
        if (!cancelled) setWaveformFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [fileId]);

  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || width === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const height = canvas.clientHeight;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const played = isDark ? '#60a5fa' : '#2563eb';
    const remaining = isDark ? '#4b5563' : '#cbd5e1';

    const slot = BAR_WIDTH + BAR_GAP;
    const barCount = Math.max(1, Math.floor(width / slot));
    const source = peaks ?? [];
    const mid = height / 2;
    const playedX = progress * width;

    for (let i = 0; i < barCount; i++) {
      const x = i * slot;
      let amp: number;
      if (source.length > 0) {
        const start = Math.floor((i / barCount) * source.length);
        const end = Math.max(start + 1, Math.floor(((i + 1) / barCount) * source.length));
        let max = 0;
        for (let j = start; j < end; j++) if (source[j] > max) max = source[j];
        amp = max;
      } else {
        // 파형 로딩 전에는 정적인 플레이스홀더 패턴
        amp = 0.18 + 0.1 * Math.abs(Math.sin(i * 0.6));
      }

      const barHeight = Math.max(2, amp * (height - 4));
      ctx.fillStyle = x + BAR_WIDTH <= playedX ? played : remaining;
      ctx.fillRect(x, mid - barHeight / 2, BAR_WIDTH, barHeight);
    }
  }, [width, peaks, progress, isDark]);

  useEffect(() => {
    drawWaveform();
  }, [drawWaveform]);

  const seekFromEvent = useCallback((clientX: number): number | null => {
    const canvas = canvasRef.current;
    if (!canvas || duration <= 0) return null;
    const rect = canvas.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return ratio * duration;
  }, [duration]);

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const time = seekFromEvent(e.clientX);
    if (time === null) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setSeekPreview(time);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (seekPreview === null) return;
    const time = seekFromEvent(e.clientX);
    if (time !== null) setSeekPreview(time);
  };

  const commitSeek = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (seekPreview === null) return;
    const time = seekFromEvent(e.clientX) ?? seekPreview;
    if (audioRef.current) audioRef.current.currentTime = time;
    setCurrentTime(time);
    setSeekPreview(null);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play();
    } else {
      audio.pause();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLCanvasElement>) => {
    const audio = audioRef.current;
    if (!audio || duration <= 0) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      audio.currentTime = Math.max(0, audio.currentTime - 5);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      audio.currentTime = Math.min(duration, audio.currentTime + 5);
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      togglePlay();
    }
  };

  const showCover = coverUrl && !coverFailed;
  const heading = title || fileName;
  // 태그가 전부 동일한 파일이 흔해 중복 표기를 걸러낸다
  const subtitle = [...new Set([artist, album].filter((v): v is string => !!v && v !== heading))].join(' · ');

  return (
    <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
      {showCover && (
        <div className="relative shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-md overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
          <img
            src={coverUrl}
            alt={fileName}
            className="w-full h-full object-cover"
            onError={() => setCoverFailed(true)}
          />
        </div>
      )}

      <div ref={containerRef} className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? '일시정지' : '재생'}
            className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          >
            {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
          </button>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {heading}
            </p>
            {subtitle && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              const audio = audioRef.current;
              if (!audio) return;
              audio.muted = !audio.muted;
              setMuted(audio.muted);
            }}
            aria-label={muted ? '음소거 해제' : '음소거'}
            className="shrink-0 p-2 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>

        <div className="relative mt-3">
          <canvas
            ref={canvasRef}
            role="slider"
            tabIndex={0}
            aria-label="재생 위치"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(displayTime)}
            aria-valuetext={`${formatTime(displayTime)} / ${formatTime(duration)}`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={commitSeek}
            onPointerCancel={() => setSeekPreview(null)}
            onKeyDown={handleKeyDown}
            className="w-full h-14 sm:h-16 cursor-pointer touch-none outline-none rounded focus-visible:ring-2 focus-visible:ring-blue-500"
          />
          {!peaks && !waveformFailed && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            </div>
          )}
        </div>

        <div className="flex justify-between mt-1 text-xs tabular-nums text-gray-500 dark:text-gray-400">
          <span>{formatTime(displayTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      <audio
        ref={audioRef}
        src={`/api/stream/${fileId}`}
        preload="metadata"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setDuration(d);
        }}
        onTimeUpdate={(e) => {
          if (seekPreview === null) setCurrentTime(e.currentTarget.currentTime);
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        className="hidden"
      />
    </div>
  );
}
